<template>
  <div class="cost-center-attribution">

    <div class="d-flex justify-space-between align-center mb-5">
      <h2>{{ $t('cost-center.attribution-title') }}</h2>
      <div class="d-flex align-center">
        <v-btn text small @click="goBack">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('back') }}
        </v-btn>
        <audit-trail-button class="ml-2" entity-type="CostCenter" :organization-id="$route.params.id" />
      </div>
    </div>

    <div class="attribution-layout" :class="{ 'with-editor': showEditor }">
      <!-- Colonne gauche : Liste des cost centers -->
      <div class="attribution-sidebar">
        <cost-center-attribution-sidebar
          ref="sidebar"
          :organization-id="organizationId"
          :selected-cost-center-id="selectedCostCenterId"
          :selected-entities-count="selectedEntities.length"
          :assign-mode="assignMode"
          :operation-in-progress="operationInProgress"
          @select="onCostCenterSelect"
          @start-assign="startAssignMode"
          @start-unassign="startUnassignMode"
          @cancel-mode="cancelMode"
          @apply-mode="applyMode"
          @cost-center-created="loadCostCenters"
          @cost-center-updated="loadCostCenters"
          @cost-center-deleted="loadCostCenters"
        />
      </div>

      <!-- Zone centrale : Liste des entités -->
      <div class="attribution-main">
        <cost-center-attribution-entities-list
          ref="entitiesList"
          :organization-id="organizationId"
          :selected-cost-center="selectedCostCenter"
          :selected-entities="selectedEntities"
          :assign-mode="assignMode"
          :selected-entity-id="editorEntity ? editorEntity.id : null"
          @select="onEntitiesSelect"
          @open-editor="onOpenEditor"
          @close-editor="onEditorCancel"
        />
      </div>

      <!-- Panneau latéral : Ajustement des parts -->
      <div class="attribution-editor">
        <cost-center-attribution-editor
          v-if="showEditor"
          :organization-id="organizationId"
          :entity="editorEntity"
          :cost-centers="allCostCenters"
          :saving="editorSaving"
          @save="onEditorSave"
          @cancel="onEditorCancel"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import CostCenterAttributionSidebar from './Attribution/CostCenterAttributionSidebar.vue'
import CostCenterAttributionEntitiesList from './Attribution/CostCenterAttributionEntitiesList.vue'
import CostCenterAttributionEditor from './Attribution/CostCenterAttributionEditor.vue'
import CostCenterRepository from '@repository/CostCenterRepository'

export default {
  components: {
    AuditTrailButton,
    CostCenterAttributionSidebar,
    CostCenterAttributionEntitiesList,
    CostCenterAttributionEditor
  },

  data() {
    return {
      selectedCostCenterId: null,
      selectedCostCenter: null,
      selectedEntities: [],
      showEditor: false,
      editorEntity: null,
      allCostCenters: [],
      assignMode: null, // null, 'assign', or 'unassign'
      operationInProgress: false,
      editorSaving: false
    }
  },

  computed: {
    organizationId() {
      return this.$route.params.id
    }
  },

  mounted() {
    this.loadCostCenters()
    window.addEventListener('keydown', this.handleKeyNavigation)
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyNavigation)
  },

  methods: {
    loadCostCenters() {

      CostCenterRepository.search(this.organizationId, {
        offset: 0,
        limit: 1000 // Load all cost centers
      }, 'attribution-main').then(result => {
        this.allCostCenters = result.items || []
      }).catch(err => {
        this.notifyError(err)
      })
    },

    onCostCenterSelect(costCenter) {
      this.selectedCostCenterId = costCenter?.id || null
      this.selectedCostCenter = costCenter
    },

    onEntitiesSelect(entities) {
      this.selectedEntities = entities
    },

    startAssignMode() {
      if (!this.selectedCostCenter) {
        this.$dialog.notify.warning(this.$t('cost-center.select-cost-center-first'))
        return
      }
      this.assignMode = 'assign'
      this.selectedEntities = []
    },

    startUnassignMode() {
      if (!this.selectedCostCenter) {
        this.$dialog.notify.warning(this.$t('cost-center.select-cost-center-first'))
        return
      }
      this.assignMode = 'unassign'
      this.selectedEntities = []
    },

    cancelMode() {
      this.assignMode = null
      this.selectedEntities = []
    },

    applyMode() {
      if (this.selectedEntities.length === 0) {
        this.$dialog.notify.warning(this.$t('cost-center.select-entities-first'))
        return
      }
      // Start loading
      this.operationInProgress = true

      // Format entities for backend
      const data = {
        entities: this.selectedEntities.map(entity => ({
          type: entity.type,
          id: entity.id
        }))
      }

      // Choose the right repository method
      const repositoryMethod = this.assignMode === 'assign'
        ? CostCenterRepository.assign
        : CostCenterRepository.unassign

      // Call API
      repositoryMethod(this.organizationId, this.selectedCostCenter.id, data)
        .then(() => {
          const action = this.assignMode === 'assign' ? 'assigned' : 'unassigned'
          this.$dialog.notify.success(
            this.$t(`cost-center.batch-${action}-success`, {
              count: this.selectedEntities.length,
              costCenter: this.selectedCostCenter.name
            })
          )

          // If unassign mode, activate "total not 100%" filter to highlight broken allocations
          const wasUnassignMode = this.assignMode === 'unassign'

          // Reset mode
          this.assignMode = null
          this.selectedEntities = []

          // Reload cost centers to update counters
          if (this.$refs.sidebar) {
            this.$refs.sidebar.loadCostCenters()
          }

          // Reload entities list to refresh the display and update editor if needed
          if (this.$refs.entitiesList) {
            this.$refs.entitiesList.loadEntitiesCounts()

            // Wait for entities to reload, then update the editor if an entity is displayed
            this.$refs.entitiesList.loadEntities().then(() => {
              // If the editor is showing an entity, refresh it with updated data
              if (this.editorEntity) {
                const updatedEntity = this.$refs.entitiesList.getEntityById(this.editorEntity.id, this.editorEntity.type)

                if (updatedEntity) {
                  // Update editor with fresh data
                  this.editorEntity = {
                    ...updatedEntity,
                    action: 'adjust'
                  }
                } else {
                  this.showEditor = false
                  this.editorEntity = null
                }
              }

              // Auto-activate "total not 100%" filter after unassign (only if there are results)
              if (wasUnassignMode) {
                this.$nextTick(() => {
                  const count = this.$refs.entitiesList.countEntitiesWithInvalidTotal()
                  if (count > 0) {
                    this.$refs.entitiesList.activateTotalNot100Filter()
                  }
                })
              }
            })
          }
        })
        .catch(err => {
          this.notifyError(err)
        })
        .finally(() => {
          // Stop loading
          this.operationInProgress = false
        })
    },

    onOpenEditor(entity) {
      // Open editor directly when clicking on a row
      this.editorEntity = {
        ...entity,
        action: 'adjust'
      }
      this.showEditor = true
    },

    onEditorSave(data) {

      const { entity, allocations } = data

      // Start saving
      this.editorSaving = true

      // Filter out deleted allocations and format for backend
      const activeCostCenters = allocations
        .filter(alloc => !alloc.isDeleted)
        .map(alloc => ({
          costCenterId: alloc.costCenterId,
          sharePercent: alloc.sharePercent
        }))

      // Call API to update entity allocations
      CostCenterRepository.updateEntityAllocations(
        this.organizationId,
        entity.type,
        entity.id,
        activeCostCenters
      )
        .then(() => {
          this.$dialog.notify.success(
            this.$t('cost-center.updated')
          )

          // Reload cost centers to update counters
          if (this.$refs.sidebar) {
            this.$refs.sidebar.loadCostCenters()
          }

          // Reload entities list to refresh the display and update editor with fresh data
          if (this.$refs.entitiesList) {
            this.$refs.entitiesList.loadEntitiesCounts()

            // Wait for entities to reload, then update the editor with fresh data
            this.$refs.entitiesList.loadEntities().then(() => {
              // Find the updated entity in the refreshed list
              const updatedEntity = this.$refs.entitiesList.getEntityById(entity.id, entity.type)

              if (updatedEntity) {
                // Update editor with fresh data
                this.editorEntity = {
                  ...updatedEntity,
                  action: 'adjust'
                }
              } else {
                this.showEditor = false
                this.editorEntity = null
              }
            }).catch(() => {
              // On error, close the editor
              this.showEditor = false
              this.editorEntity = null
            })
          }
        })
        .catch(err => {
          this.notifyError(err)
        })
        .finally(() => {
          this.editorSaving = false
        })
    },

    onEditorCancel() {
      this.showEditor = false
      this.editorEntity = null
    },

    goBack() {
      this.$router.push({
        name: 'organizations.detail.cost-centers',
        params: {
          id: this.organizationId
        }
      })
    },

    handleKeyNavigation(event) {
      // Only handle arrow keys when editor is open
      if (!this.showEditor || !this.editorEntity) return

      // Don't intercept if user is typing in an input
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault()

        const entitiesList = this.$refs.entitiesList
        if (!entitiesList) return

        const filteredEntities = entitiesList.filteredEntities
        if (!filteredEntities || filteredEntities.length === 0) return

        // Find current entity index
        const currentIndex = filteredEntities.findIndex(
          e => e.id === this.editorEntity.id && e.type === this.editorEntity.type
        )

        if (currentIndex === -1) return

        // Calculate next index
        let nextIndex
        if (event.key === 'ArrowUp') {
          nextIndex = currentIndex - 1
          if (nextIndex < 0) nextIndex = filteredEntities.length - 1 // Loop to end
        } else {
          nextIndex = currentIndex + 1
          if (nextIndex >= filteredEntities.length) nextIndex = 0 // Loop to start
        }

        // Open editor with next entity
        const nextEntity = filteredEntities[nextIndex]
        if (nextEntity) {
          this.onOpenEditor(nextEntity)

          // Scroll the selected row into view (only inside the table wrapper, not the whole page)
          this.$nextTick(() => {
            const tableWrapper = document.querySelector('.entities-table .v-data-table__wrapper')
            const selectedRow = document.querySelector('.entities-table .selected-entity-row')
            if (tableWrapper && selectedRow) {
              const wrapperRect = tableWrapper.getBoundingClientRect()
              const rowRect = selectedRow.getBoundingClientRect()

              // Calculate if row is outside visible area
              const rowTop = rowRect.top - wrapperRect.top
              const rowBottom = rowRect.bottom - wrapperRect.top

              if (rowTop < 0) {
                // Row is above visible area, scroll up
                tableWrapper.scrollTop += rowTop - 20 // 20px margin
              } else if (rowBottom > wrapperRect.height) {
                // Row is below visible area, scroll down
                tableWrapper.scrollTop += rowBottom - wrapperRect.height + 20 // 20px margin
              }
            }
          })
        }
      }
    }
  }
}
</script>

<style lang="scss">
.cost-center-attribution {
  .attribution-layout {
    display: grid;
    grid-template-columns: 30% 1fr 0;
    gap: 20px;
    max-height: calc(100vh - 300px);
    transition: grid-template-columns 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .attribution-layout.with-editor {
    grid-template-columns: 30% 1fr 450px;
  }

  .attribution-sidebar,
  .attribution-main {
    background: white;
    border-radius: 4px;
    padding: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .attribution-editor {
    background: white;
    border-radius: 4px;
    padding: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    position: sticky;
    top: 20px;
    max-height: calc(100vh - 240px);
    overflow-x: hidden;
    overflow-y: auto;
    width: 450px;

    // Positionner hors écran à droite par défaut
    transform: translateX(calc(100% + 20px));
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  // Quand l'éditeur est visible, le faire glisser vers la gauche
  .attribution-layout.with-editor .attribution-editor {
    transform: translateX(0);
  }
}
</style>
